/**
 * <bk-ai-menu> — the design's "Use with AI" menu (markup: snippets/developer-ai-menu.liquid).
 *
 * - Toggle button opens/closes the panel (aria-expanded); Escape or a click outside closes it.
 * - Copy as Markdown copies the inline <script type="text/markdown" id="page-md">, shows
 *   "Copied" + check icon for 2s and announces data-status.
 * - Open in Claude / ChatGPT: prompt = data-prompt-intro + "\n\n" + Markdown. If
 *   encodeURIComponent(prompt).length <= 7000 it goes in the URL; otherwise the short prompt
 *   (data-prompt-short) is used and the Markdown is copied when the link is clicked.
 *   (Exact rule from the design's resource/guide boards.)
 * - View as Markdown: Shopify serves ?view=markdown as text/html, so a browser runs the lines
 *   together and hides <placeholders>. A plain click opens the same Markdown as a text/plain
 *   page instead; the link (for machines, new-tab clicks and no JS) stays ?view=markdown.
 */

/* Same two helpers as bk-copy.js, kept local so this file loads on its own (one request). */
function legacyCopy(text) {
  // Fallback for browsers/embedded panes that refuse the async Clipboard API.
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;';
  document.body.appendChild(area);
  const active = document.activeElement;
  area.select();
  let ok = false;
  try {
    ok = document.execCommand('copy');
  } catch (error) {
    ok = false;
  }
  area.remove();
  if (active && active.focus) active.focus();
  return ok;
}

/** Copy text; resolves true only if it really reached the clipboard. */
function copyText(text) {
  if (!text) return Promise.resolve(false);
  if (navigator.clipboard && navigator.clipboard.writeText && window.isSecureContext) {
    return navigator.clipboard.writeText(text).then(
      () => true,
      () => legacyCopy(text)
    );
  }
  return Promise.resolve(legacyCopy(text));
}

function showIcon(icon, visible) {
  if (icon) icon.style.display = visible ? '' : 'none';
}

const MAX_URL_PROMPT = 7000;
const RESET_MS = 2000;

class BkAiMenu extends HTMLElement {
  connectedCallback() {
    this.toggleButton = this.querySelector('.bk-ai-btn');
    this.panel = this.querySelector('.bk-ai-menu');
    this.status = this.querySelector('[data-ai-status]');
    this.copyButton = this.querySelector('[data-ai-copy]');
    if (!this.toggleButton || !this.panel) return;

    this.markdown = this.readMarkdown();
    this.preparePrompts();

    this.toggleButton.addEventListener('click', () => this.setOpen(!this.isOpen));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        this.setOpen(false);
        this.toggleButton.focus();
      }
    });
    this.onDocumentClick = (event) => {
      if (this.isOpen && !this.contains(event.target)) this.setOpen(false);
    };
    document.addEventListener('click', this.onDocumentClick);

    if (this.copyButton) this.copyButton.addEventListener('click', () => this.copyMarkdown());
    this.querySelectorAll('[data-ai-close]').forEach((link) => {
      link.addEventListener('click', () => this.setOpen(false));
    });
    this.querySelectorAll('[data-ai-view]').forEach((link) => {
      link.addEventListener('click', (event) => {
        if (!this.markdown || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        try {
          const url = URL.createObjectURL(new Blob([this.markdown], { type: 'text/plain;charset=utf-8' }));
          const tab = window.open(url, '_blank');
          if (!tab) return; // pop-up blocked: let the link open ?view=markdown
          event.preventDefault();
          setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (error) {
          /* Blob URLs unavailable: the link opens ?view=markdown. */
        }
      });
    });
    this.querySelectorAll('[data-ai-open]').forEach((link) => {
      link.addEventListener('click', () => {
        if (!this.fits) copyText(this.markdown);
        this.setOpen(false);
      });
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    clearTimeout(this.timer);
  }

  get isOpen() {
    return this.toggleButton.getAttribute('aria-expanded') === 'true';
  }

  /** Keep the panel inside the viewport: the stylesheet anchors it to one edge of the button,
   *  which overflows when the button sits on the other side (e.g. ~600px wide windows). */
  keepInView() {
    const p = this.panel;
    p.style.left = '';
    p.style.right = '';
    const gutter = 16;
    const r = p.getBoundingClientRect();
    if (r.right > window.innerWidth - gutter) {
      p.style.left = 'auto';
      p.style.right = '0';
    } else if (r.left < gutter) {
      p.style.left = '0';
      p.style.right = 'auto';
    }
  }

  setOpen(open) {
    this.toggleButton.setAttribute('aria-expanded', open ? 'true' : 'false');
    this.panel.hidden = !open;
    if (open) this.keepInView();
  }

  readMarkdown() {
    const source = document.getElementById(this.dataset.markdown || 'page-md');
    if (!source) return '';
    // The snippet escapes "</script" and "<!--" so the Markdown cannot end its <script>; undo that.
    return source.textContent.replace(/<\\\/script/gi, '</script').replace(/<\\!--/g, '<!--');
  }

  preparePrompts() {
    const fullPrompt = (this.dataset.promptIntro || '') + '\n\n' + this.markdown;
    this.fits = encodeURIComponent(fullPrompt).length <= MAX_URL_PROMPT;
    const prompt = this.fits ? fullPrompt : this.dataset.promptShort || '';
    this.querySelectorAll('[data-ai-open]').forEach((link) => {
      link.href = link.getAttribute('data-ai-open') + encodeURIComponent(prompt);
    });
    const description = this.fits ? this.dataset.openFits : this.dataset.openCopies;
    this.querySelectorAll('[data-ai-open-desc]').forEach((node) => {
      node.textContent = description || '';
    });
  }

  copyMarkdown() {
    copyText(this.markdown).then((ok) => {
      this.renderCopied(ok ? true : 'failed');
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.renderCopied(false), RESET_MS);
    });
  }

  renderCopied(state) {
    const copied = state === true;
    const label = this.copyButton.querySelector('[data-copy-label]');
    if (label) {
      if (this.idleLabel === undefined) this.idleLabel = label.textContent;
      label.textContent = copied
        ? this.dataset.copiedLabel || 'Copied'
        : state === 'failed'
          ? this.dataset.copyFailedLabel || 'Couldn’t copy. Use “View as Markdown”.'
          : this.idleLabel;
    }
    showIcon(this.copyButton.querySelector('[data-copy-icon="idle"]'), !copied);
    showIcon(this.copyButton.querySelector('[data-copy-icon="done"]'), copied);
    if (this.status) this.status.textContent = copied ? this.dataset.status || '' : state === 'failed' ? this.dataset.copyFailedLabel || 'Couldn’t copy' : '';
  }
}

if (!customElements.get('bk-ai-menu')) customElements.define('bk-ai-menu', BkAiMenu);
